import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import {
  PAGE_WIDTH_PX,
  PAGE_HEIGHT_PX,
  RENDER_DPI,
  getLayoutInPixels,
} from '../lib/worksheetLayout';
import { measureTextWidth } from '../lib/fontCalibration';

// Font family for tracing
const TRACING_FONT = 'SsTraceTogether';

/**
 * WorksheetPreview Component
 * 
 * Renders the worksheet using SVG overlay on template image.
 * Guide lines and text scale proportionally when text exceeds row width.
 */
export const WorksheetPreview = ({ 
  name = '', 
  templatePath = '/templates/template1.png',
  debugMode = false,
  onFontSizeCalculated = () => {}
}) => {
  const containerRef = useRef(null);
  const [fontLoaded, setFontLoaded] = useState(false);
  
  // Get layout in pixels at render DPI
  const layout = getLayoutInPixels(RENDER_DPI);
  
  // Load custom font
  useEffect(() => {
    const loadFont = async () => {
      try {
        const font = new FontFace(TRACING_FONT, `url(/fonts/SsTraceTogether-Regular.ttf)`);
        const loadedFont = await font.load();
        document.fonts.add(loadedFont);
        await document.fonts.ready;
        setFontLoaded(true);
      } catch (error) {
        console.error('Failed to load tracing font:', error);
        setFontLoaded(true);
      }
    };
    loadFont();
  }, []);
  
  // Convert pixel position to percentage
  const pxToPercent = (px, total) => (px / total) * 100;
  
  // Calculate the scale factor based on text width vs available width
  const scaleFactor = useMemo(() => {
    if (!name.trim() || !fontLoaded) return 1;
    
    const row = layout.rows[0];
    const rowWidthPx = row.widthPx;
    const lineHeightPx = row.lineHeightPx;
    
    // Base font size (fills the line height) - 1.56 = 10% larger than 1.42
    const baseFontSizePx = lineHeightPx * 1.56;
    
    // Measure actual text width at base font size
    const textWidthPx = measureTextWidth(name, baseFontSizePx, TRACING_FONT);
    
    // Calculate scale factor if text is wider than available space
    const availableWidthPx = rowWidthPx * 0.98; // 2% margin
    
    if (textWidthPx > availableWidthPx) {
      return Math.max(availableWidthPx / textWidthPx, 0.25); // Don't go below 25%
    }
    
    return 1;
  }, [name, fontLoaded, layout.rows]);
  
  // Notify parent of calculated font size
  useEffect(() => {
    const row = layout.rows[0];
    const baseFontSizePx = row.lineHeightPx * 1.35;
    const actualFontSize = baseFontSizePx * scaleFactor;
    onFontSizeCalculated(actualFontSize);
  }, [scaleFactor, layout.rows, onFontSizeCalculated]);
  
  // Calculate scaled line positions for a row
  const getScaledRowLines = (row) => {
    const centerY = (row.topLinePx + row.bottomLinePx) / 2;
    const halfHeight = (row.bottomLinePx - row.topLinePx) / 2;
    
    // Scale the distance from center
    const scaledHalfHeight = halfHeight * scaleFactor;
    
    return {
      topLinePx: centerY - scaledHalfHeight,
      midLinePx: centerY,
      bottomLinePx: centerY + scaledHalfHeight,
      leftPx: row.leftPx,
      widthPx: row.widthPx
    };
  };
  
  // Get text style with scaled font size
  const getRowTextStyle = (row) => {
    const scaledRow = getScaledRowLines(row);
    const topPercent = pxToPercent(scaledRow.topLinePx, PAGE_HEIGHT_PX);
    const bottomPercent = pxToPercent(scaledRow.bottomLinePx, PAGE_HEIGHT_PX);
    const leftPercent = pxToPercent(row.leftPx, PAGE_WIDTH_PX);
    const widthPercent = pxToPercent(row.widthPx, PAGE_WIDTH_PX);
    const heightPercent = bottomPercent - topPercent;
    
    // Base font size as percentage of page height, then scaled
    const lineHeightPercent = pxToPercent(row.lineHeightPx, PAGE_HEIGHT_PX);
    const baseFontSizePercent = lineHeightPercent * 1.56;
    const fontSizePercent = baseFontSizePercent * scaleFactor;
    
    return {
      position: 'absolute',
      top: `${topPercent}%`,
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
      height: `${heightPercent}%`,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      fontFamily: "'SsTraceTogether', sans-serif",
      fontSize: `${fontSizePercent}cqh`,
      color: '#333333',
      lineHeight: 0.72,
      pointerEvents: 'none',
      zIndex: 20,
      transform: 'translateY(8%)',
      whiteSpace: 'nowrap',
      overflow: 'visible'
    };
  };
  
  // Get scaled lines for each row
  const scaledRows = layout.rows.map(row => getScaledRowLines(row));
  
  return (
    <div 
      ref={containerRef}
      className="w-full h-full relative"
      style={{ 
        aspectRatio: '8.5 / 11',
        containerType: 'size'
      }}
    >
      {/* Template background */}
      <img
        src={templatePath}
        alt="Worksheet template"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'fill',
          zIndex: 1
        }}
        crossOrigin="anonymous"
      />
      
      {/* SVG overlay for scaled guide lines */}
      <svg
        viewBox={`0 0 ${PAGE_WIDTH_PX} ${PAGE_HEIGHT_PX}`}
        preserveAspectRatio="none"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 10
        }}
      >
        {/* Row 0 Guide lines - scaled */}
        <line
          x1={scaledRows[0].leftPx}
          y1={scaledRows[0].topLinePx}
          x2={scaledRows[0].leftPx + scaledRows[0].widthPx}
          y2={scaledRows[0].topLinePx}
          stroke="#666666"
          strokeWidth={4 * scaleFactor}
        />
        <line
          x1={scaledRows[0].leftPx}
          y1={scaledRows[0].midLinePx}
          x2={scaledRows[0].leftPx + scaledRows[0].widthPx}
          y2={scaledRows[0].midLinePx}
          stroke="#999999"
          strokeWidth={3 * scaleFactor}
          strokeDasharray={`${16 * scaleFactor} ${12 * scaleFactor}`}
        />
        <line
          x1={scaledRows[0].leftPx}
          y1={scaledRows[0].bottomLinePx}
          x2={scaledRows[0].leftPx + scaledRows[0].widthPx}
          y2={scaledRows[0].bottomLinePx}
          stroke="#666666"
          strokeWidth={4 * scaleFactor}
        />
        
        {/* Row 1 Guide lines - scaled */}
        <line
          x1={scaledRows[1].leftPx}
          y1={scaledRows[1].topLinePx}
          x2={scaledRows[1].leftPx + scaledRows[1].widthPx}
          y2={scaledRows[1].topLinePx}
          stroke="#666666"
          strokeWidth={4 * scaleFactor}
        />
        <line
          x1={scaledRows[1].leftPx}
          y1={scaledRows[1].midLinePx}
          x2={scaledRows[1].leftPx + scaledRows[1].widthPx}
          y2={scaledRows[1].midLinePx}
          stroke="#999999"
          strokeWidth={3 * scaleFactor}
          strokeDasharray={`${16 * scaleFactor} ${12 * scaleFactor}`}
        />
        <line
          x1={scaledRows[1].leftPx}
          y1={scaledRows[1].bottomLinePx}
          x2={scaledRows[1].leftPx + scaledRows[1].widthPx}
          y2={scaledRows[1].bottomLinePx}
          stroke="#666666"
          strokeWidth={4 * scaleFactor}
        />
        
        {/* Row 2 Guide lines - scaled */}
        <line
          x1={scaledRows[2].leftPx}
          y1={scaledRows[2].topLinePx}
          x2={scaledRows[2].leftPx + scaledRows[2].widthPx}
          y2={scaledRows[2].topLinePx}
          stroke="#666666"
          strokeWidth={4 * scaleFactor}
        />
        <line
          x1={scaledRows[2].leftPx}
          y1={scaledRows[2].midLinePx}
          x2={scaledRows[2].leftPx + scaledRows[2].widthPx}
          y2={scaledRows[2].midLinePx}
          stroke="#999999"
          strokeWidth={3 * scaleFactor}
          strokeDasharray={`${16 * scaleFactor} ${12 * scaleFactor}`}
        />
        <line
          x1={scaledRows[2].leftPx}
          y1={scaledRows[2].bottomLinePx}
          x2={scaledRows[2].leftPx + scaledRows[2].widthPx}
          y2={scaledRows[2].bottomLinePx}
          stroke="#666666"
          strokeWidth={4 * scaleFactor}
        />
        
        {/* Debug overlays */}
        {debugMode && (
          <g>
            <rect
              x={layout.tracingRect.left}
              y={layout.tracingRect.top}
              width={layout.tracingRect.width}
              height={layout.tracingRect.height}
              fill="none"
              stroke="blue"
              strokeWidth={4}
              strokeDasharray="20 20"
            />
          </g>
        )}
      </svg>
      
      {/* HTML text overlay */}
      {name.trim() && fontLoaded && (
        <>
          <div style={getRowTextStyle(layout.rows[0])} data-testid="name-row-0">
            {name}
          </div>
          <div style={getRowTextStyle(layout.rows[1])} data-testid="name-row-1">
            {name}
          </div>
          <div style={getRowTextStyle(layout.rows[2])} data-testid="name-row-2">
            {name}
          </div>
        </>
      )}
    </div>
  );
};

export default WorksheetPreview;
